<script setup lang="ts">
import { eventBus } from "#bb/lib/eventBus";
import { cn } from "@/lib/utils";
import { useTemplating } from "#bb/composables/useTemplating";
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
import { resolveCandidateScopedFileApiPath } from "#bb/lib/pathContract";

/** 1) Expect `id` from the parent. */
const props = defineProps<{ id: string }>();

/** 2) Access the store's read method to get building-block data. */
const { getContentById, getResolvedDataPath } = useContentManager();
const definitionData = computed(() => getContentById(props.id) || {});

/** 3) Define context menu commands so the user can open your editor. */
const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "File upload editor",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit the file upload settings",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

/** 5) Expose commands & properties so the context menu can pick them up. */
defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "File Upload",
    icon: "lucide:upload",
  },
});

const route = useRoute();
const { siteId } = useSite();
const { profile } = useProfile();
const userOverrideId = computed(() => {
  const value = route.query.user;
  if (Array.isArray(value)) return value[0] ?? "";
  return typeof value === "string" ? value : "";
});
const profileId = computed(() => userOverrideId.value || profile.value?.id || "");

const templateContext = computed(() => ({
  user: profile.value,
  userProfileId: profileId.value,
  siteId: siteId.value,
}));
const { applyTemplate } = useTemplating(templateContext, { silent: true });

function resolveTemplate(value: string | undefined) {
  if (!value) return "";
  if (!value.includes("{{")) return value;
  if (!profileId.value) return value;
  return applyTemplate(value);
}

const resolvedContainerPath = computed(() =>
  resolveTemplate(definitionData.value.containerPath)
);
const resolvedPath = computed(() => resolveTemplate(definitionData.value.path));

const storageResolution = computed(() => {
  try {
    return {
      path: resolveCandidateScopedFileApiPath(
        resolvedContainerPath.value,
        resolvedPath.value,
        profileId.value,
        "Upload Documents storage path",
      ),
      error: null,
    };
  } catch (error) {
    return {
      path: "",
      error: error instanceof Error
        ? error.message
        : "Upload Documents storage path could not be resolved",
    };
  }
});
const folderPath = computed(() => storageResolution.value.path);
const storageError = computed(() => storageResolution.value.error);

function createEmptyFolder(path: string) {
  const segments = path.split("/").filter(Boolean);
  const name = segments[segments.length - 1] || "";
  return {
    id: "",
    parentId: "",
    siteId: siteId.value ?? "",
    name,
    slug: "",
    path,
    type: "text/directory",
    length: null,
    children: [],
    versions: [],
    permissions: [],
    metadata: {},
    parents: [],
  };
}

const { getAnswer, setAnswer } = useAnswers();

const answerPayload = computed(() => ({
  type: ComponentDefinitionIds.UPLOAD_DOCUMENTS,
  value: folderPath.value,
  path: getResolvedDataPath(props.id).join("/"),
  binding: definitionData.value,
  data: {
    userProfileId: profileId.value,
    siteId: siteId.value,
    containerId: definitionData.value.containerId,
    containerPath: resolvedContainerPath.value,
    path: resolvedPath.value,
    storagePath: folderPath.value,
  },
}));

const ensureAnswer = async () => {
  if (!props.id || storageError.value) return;
  const payload = answerPayload.value;
  const existing = await getAnswer(props.id, payload.path);
  if (
    !existing ||
    !existing.data?.storagePath ||
    existing.data?.storagePath !== payload.data.storagePath ||
    existing.data?.userProfileId !== payload.data.userProfileId ||
    existing.data?.containerPath !== payload.data.containerPath ||
    existing.data?.path !== payload.data.path
  ) {
    await setAnswer(props.id, payload);
  }
};

const { get } = useFileEntries(siteId);
const siteFilesState = reactive<{ files: any; folderPath: string }>({
  files: null,
  folderPath: folderPath.value,
});

async function loadFiles(path: string) {
  siteFilesState.folderPath = path;
  const entries = await get(path, "flags,metadata", {
    suppressNotFoundToast: true,
  });
  siteFilesState.files = entries || createEmptyFolder(path);
}

if (folderPath.value) {
  await loadFiles(folderPath.value);
}

// An empty action list with navigation off means the block is a drop zone only.
const dropZoneOnly = computed(
  () =>
    Array.isArray(definitionData.value.actionFilter) &&
    definitionData.value.actionFilter.length === 0 &&
    !definitionData.value.enableNavigation
);

const showEmptyState = computed(() => {
  if (dropZoneOnly.value) return false;
  const current = siteFilesState.files;
  if (!current) return false;
  const children = Array.isArray(current.children) ? current.children : [];
  return children.length === 0;
});

onMounted(() => {
  ensureAnswer();
  watch([folderPath, resolvedContainerPath, resolvedPath, profileId], () => {
    ensureAnswer();
  });
  watch(folderPath, (newPath) => {
    if (newPath) {
      loadFiles(newPath);
    }
  });
});

provide("siteFiles", siteFilesState);
</script>

<template>
  <!-- The `upload-documents` requires containerId, path, etc. as props. 
       We pass those from definitionData. -->
  <div :class="cn($attrs.class ?? '')">
    <div
      v-if="storageError"
      class="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
      role="alert"
    >
      {{ storageError }}
    </div>
    <site-files v-else :path="folderPath" :enable-navigation="definitionData.enableNavigation" :show-back-button="definitionData.showBackButton"
      :include-mime-types="definitionData.includeMimeTypes" :exclude-mime-types="definitionData.excludeMimeTypes"
      :action-filter="definitionData.actionFilter" :hide-list="dropZoneOnly">
      <template #header>
        <site-files-drop-zone
          v-if="definitionData.showDropZone !== false"
          :path="folderPath"
          skip-permission-check
          :mkdir-on-upload="definitionData.mkdirOnUpload ?? true"
        />
      </template>
      <template #footer>
        <div
          v-if="showEmptyState"
          class="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center"
        >
          <p class="text-sm font-medium text-gray-700">No files have been uploaded yet.</p>
          <p v-if="definitionData.showDropZone !== false" class="mt-1 text-sm text-gray-500">
            You can upload a file now.
          </p>
        </div>
      </template>
    </site-files>
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIds.UPLOAD_DOCUMENTS,
  editor: () => import("#bb/components/editor/media/upload-documents.vue"),
  icon: "lucide:upload",
  type: "File Upload",
  description: "A building block for uploading files to a container/path.",
  default: {
    containerId: crypto.randomUUID(),
    containerPath: "/",
    showDropZone: true,
    mkdirOnUpload: true,
    files: [], // store an array of uploaded file objects: [{ name, url, size }, ...]
  },
  categories: ["Media"],
  order: 13050, // or any order you prefer
};
</script>
